import type {
  PlanSummary,
  PlanWithItems,
  PlanUpload,
  PlanUpdate,
  ItemCreate,
  ItemUpdate,
  ItemsBatchAdd,
  ItemsBatchEdit,
  TimelineItem,
  Comment,
  CommentCreate,
  User,
  PlanMember,
  Signup,
  Login,
  ApiTokenSummary,
} from "@travel/shared";

const BASE = "/api";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly issues?: Array<{ path: string; message: string }>,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const e =
      (data as { error?: { code?: string; message?: string; issues?: Array<{ path: string; message: string }> } })?.error ?? {};
    throw new ApiError(res.status, e.code ?? "error", e.message ?? res.statusText, e.issues);
  }
  return data as T;
}

export const api = {
  auth: {
    me: () => request<{ user: User }>("/auth/me"),
    signup: (body: Signup) =>
      request<{ user: User }>("/auth/signup", { method: "POST", body: JSON.stringify(body) }),
    login: (body: Login) =>
      request<{ user: User }>("/auth/login", { method: "POST", body: JSON.stringify(body) }),
    logout: () => request<void>("/auth/logout", { method: "POST" }),
    withdraw: () => request<void>("/auth/me", { method: "DELETE" }),
  },

  members: {
    list: (planId: string) => request<{ members: PlanMember[] }>(`/plans/${planId}/members`),
    invite: (planId: string, email: string) =>
      request<PlanMember>(`/plans/${planId}/members`, {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    remove: (planId: string, userId: string) =>
      request<void>(`/plans/${planId}/members/${userId}`, { method: "DELETE" }),
  },

  tokens: {
    list: () => request<{ tokens: ApiTokenSummary[] }>("/auth/tokens"),
    create: (label: string) =>
      request<{ token: string; summary: ApiTokenSummary }>("/auth/tokens", {
        method: "POST",
        body: JSON.stringify({ label }),
      }),
    revoke: (id: string) =>
      request<void>(`/auth/tokens/${id}`, { method: "DELETE" }),
  },

  listPlans: () => request<{ plans: PlanSummary[] }>("/plans"),
  createPlan: (body: PlanUpload) =>
    request<{ id: string }>("/plans", { method: "POST", body: JSON.stringify(body) }),
  getPlan: (id: string) => request<PlanWithItems>(`/plans/${id}`),
  updatePlan: (id: string, body: PlanUpdate) =>
    request<void>(`/plans/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deletePlan: (id: string) => request<void>(`/plans/${id}`, { method: "DELETE" }),

  createItem: (planId: string, body: ItemCreate) =>
    request<TimelineItem>(`/plans/${planId}/items`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateItem: (id: string, body: ItemUpdate) =>
    request<TimelineItem>(`/items/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteItem: (id: string) => request<void>(`/items/${id}`, { method: "DELETE" }),

  batchAddItems: (planId: string, body: ItemsBatchAdd) =>
    request<{ items: TimelineItem[] }>(`/plans/${planId}/items/batch`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  batchEditItems: (planId: string, body: ItemsBatchEdit) =>
    request<{ items: TimelineItem[] }>(`/plans/${planId}/items/batch`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  listComments: (itemId: string) => request<{ comments: Comment[] }>(`/items/${itemId}/comments`),
  createComment: (itemId: string, body: CommentCreate) =>
    request<Comment>(`/items/${itemId}/comments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteComment: (id: string) => request<void>(`/comments/${id}`, { method: "DELETE" }),
};
